import { deleteHoaFee, fetchHoaFee, getHoaFeesByEstate, upsertHoaFee } from '@/api/hoa-fee.api';
import type { HoaFee, IHoaFee } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, computed, unref } from 'vue';

export const useHoaFee = (hoaFeeIdOrRef: MaybeRef<number | undefined>) => {
    const enabled = computed(() => !!unref(hoaFeeIdOrRef));

    return useQuery<IHoaFee>(['hoa-fee', hoaFeeIdOrRef], async () => fetchHoaFee(unref(hoaFeeIdOrRef)!), {
        enabled,
        initialData: undefined,
    });
};

export const useHoaFeesByEstate = (estateIdOrRef: MaybeRef<number | undefined>) => {
    const enabled = computed(() => !!unref(estateIdOrRef));

    return useQuery<HoaFee[]>(['hoa-fees', 'by-estate', estateIdOrRef], async () => getHoaFeesByEstate(unref(estateIdOrRef)!), {
        enabled,
        initialData: [],
    });
};

export const useUpsertHoaFee = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: upsertHoaFee,
        onSuccess: (data: IHoaFee) => {
            // Invalidate and refetch HOA fees for the estate
            queryClient.invalidateQueries({
                queryKey: ['hoa-fees', 'by-estate', data.estateId],
            });

            // Invalidate the specific HOA fee
            queryClient.invalidateQueries({
                queryKey: ['hoa-fee', data.hoaFeeId],
            });
        },
    });
};

export const useDeleteHoaFee = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteHoaFee,
        onSuccess: (_, hoaFeeId) => {
            // Invalidate all HOA fee queries since we don't know which estate this belonged to
            queryClient.invalidateQueries({
                queryKey: ['hoa-fees'],
            });

            // Invalidate the specific HOA fee
            queryClient.invalidateQueries({
                queryKey: ['hoa-fee', hoaFeeId],
            });
        },
    });
};
